import React from 'react';
import {
  TwtPackageFloorplanAddOns,
  TwtPackageFloorplanInclude, TwtPackageNucleusInclude,
  TwtPackagePhotoAddOns,
  TwtPackagePhotoInclude,
  TwtPackageTimeOfDay
} from '~/tenants/twt/package/common';
import {
  TenantPackageAddOnTemplate,
  TenantPackageIncludes,
} from '~/tenants/common/package';
import { TenantPerformableAddOns } from '~/tenants/common/performable';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { TwtOrderType } from '~/tenants/twt/model/enums';
import TwtNucleusAddOns from '~/tenants/twt/performable/nucleus/TwtNucleusAddOns';
import TwtNucleusConfig from '~/tenants/twt/performable/nucleus/TwtNucleusConfig';
import { PackageFormNested } from '~/components/performable/PackageForm';
import TwtDetached from '~/tenants/twt/forms/TwtDetached';
import TwtMatterportVideoConfig from '~/tenants/twt/performable/matterport-video/TwtMatterportVideoConfig';
import InputCheckbox from '~/components/input/InputCheckbox';
import PerformableGroupForm, { usePerformableGroup } from '~/components/performable/PerformableGroupForm';
import TwtNucleusVideoConfig from '~/tenants/twt/performable/nucleus-video/TwtNucleusVideoConfig';
import { TWT_NUCLEUS_VIDEO_PRICE } from '~/tenants/twt/performable/nucleus-video/TwtNucleusVideoJob';
import TwtAerialConfig, { TwtAerialType } from '~/tenants/twt/performable/aerial/TwtAerialConfig';
import { TWT_NUCLEUS_AERIAL_VIDEO_REVENUE } from '~/tenants/twt/performable/aerial/TwtAerialJob';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import TwtNucleusGuidedConfig from '~/tenants/twt/performable/nucleus-guided/TwtNucleusGuidedConfig';
import TwtNucleusGuidedForm from '~/tenants/twt/performable/nucleus-guided/TwtNucleusGuidedForm';
import TwtNucleusHotspotsConfig from '~/tenants/twt/performable/nucleus-hotspots/TwtNucleusHotspotsConfig';
import TwtNucleusHotspotsForm from '~/tenants/twt/performable/nucleus-hotspots/TwtNucleusHotspotsForm';
import TwtNucleusJob from '~/tenants/twt/performable/nucleus/TwtNucleusJob';

export default function TwtNucleusTourForm() {
  const group = usePerformableGroup();

  return (
    <>
      <TenantPackageIncludes>
        <TwtPackagePhotoInclude />
        <TwtPackageFloorplanInclude />
        <TwtPackageNucleusInclude />
      </TenantPackageIncludes>
      <TwtPackageTimeOfDay />

      <TenantPerformableAddOns persona={PersonaType.CUSTOMER}>
        <PackageFormNested performable={TwtNucleusConfig}>
          <TwtDetached />
          <TwtNucleusAddOns persona={PersonaType.CUSTOMER} />
        </PackageFormNested>
        <TwtPackagePhotoAddOns type={TwtOrderType.FOR_SALE_LISTING} />
        <TwtPackageFloorplanAddOns />

        <TenantPackageAddOnTemplate
          htmlFor="nucleusvideo"
          title="Nucleus Video"
          description={TwtNucleusVideoConfig.short}
          image="tenant/twt/matterport/4.png"
          cost={`$${TWT_NUCLEUS_VIDEO_PRICE}.00`}
          persona={PersonaType.CUSTOMER}
          enable={
            <InputCheckbox
              id="nucleusvideo"
              checked={group.hasPerformable(TwtNucleusVideoConfig)}
              onChange={(checked) => {
                if (group.hasPerformable(TwtNucleusVideoConfig)) {
                  group.removePerformable(TwtNucleusVideoConfig);
                } else {
                  group.addPerformable(TwtNucleusVideoConfig);
                }
              }}
            />
          }
        />

        {group.hasPerformable(TwtNucleusVideoConfig) && (
          <PerformableGroupForm performable={TwtNucleusVideoConfig} />
        )}

        <TenantPackageAddOnTemplate
          htmlFor="nucleusguided"
          title="Live Virtual Tours"
          description={TwtNucleusGuidedConfig.short}
          image="tenant/twt/nucleus/live_guided.png"
          cost={`$${TwtNucleusJob.guided(25)}+`}
          persona={PersonaType.CUSTOMER}
          enable={
            <InputCheckbox
              id="nucleusguided"
              checked={group.hasPerformable(TwtNucleusGuidedConfig)}
              onChange={() => {
                if (group.hasPerformable(TwtNucleusGuidedConfig)) {
                  group.removePerformable(TwtNucleusGuidedConfig);
                } else {
                  group.addPerformable(TwtNucleusGuidedConfig, { guided: 25 });
                }
              }}
            />
          }
        />

        {group.hasPerformable(TwtNucleusGuidedConfig) && (
          <PerformableGroupForm performable={TwtNucleusGuidedConfig}>
            <TwtNucleusGuidedForm />
          </PerformableGroupForm>
        )}

        <TenantPackageAddOnTemplate
          htmlFor="nucleushotspots"
          title="Hotspots"
          description={TwtNucleusHotspotsConfig.short}
          image="tenant/twt/nucleus/hotspot.jpg"
          cost={`$${TwtNucleusJob.hotspots(20)}+`}
          persona={PersonaType.CUSTOMER}
          enable={
            <InputCheckbox
              id="nucleushotspots"
              checked={group.hasPerformable(TwtNucleusHotspotsConfig)}
              onChange={() => {
                if (group.hasPerformable(TwtNucleusHotspotsConfig)) {
                  group.removePerformable(TwtNucleusHotspotsConfig);
                } else {
                  group.addPerformable(TwtNucleusHotspotsConfig, { hotspots: 20, instructions: '' });
                }
              }}
            />
          }
        />

        {group.hasPerformable(TwtNucleusHotspotsConfig) && (
          <PerformableGroupForm performable={TwtNucleusHotspotsConfig}>
            <TwtNucleusHotspotsForm />
          </PerformableGroupForm>
        )}

        <TenantPackageAddOnTemplate
          htmlFor="aerialvideo"
          title="Aerial Video"
          description={TwtAerialConfig.short}
          image="tenant/twt/aerial/mini.jpg"
          cost={`$${TWT_NUCLEUS_AERIAL_VIDEO_REVENUE}.00`}
          persona={PersonaType.CUSTOMER}
          enable={
            <InputCheckbox
              id="aerialvideo"
              checked={group.hasPerformable(TwtAerialConfig)}
              onChange={() => {
                if (group.hasPerformable(TwtAerialConfig)) {
                  group.removePerformable(TwtAerialConfig);
                } else {
                  group.addPerformable(TwtAerialConfig, {
                    type: TwtAerialType.VIDEO,
                  });
                }
              }}
            />
          }
        />

        {group.hasPerformable(TwtAerialConfig) && (
          <PerformableGroupForm performable={TwtAerialConfig}>
            <ZodFieldHidden name="type" value={TwtAerialType.VIDEO} />
          </PerformableGroupForm>
        )}
      </TenantPerformableAddOns>
    </>
  );
}
